import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5radar from '@amcharts/amcharts5/radar';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { DashboardBaseChart } from '../shared/dashboard-base-chart';
import { DashboardBaseComponent } from '../shared/dashboard-base-component';
import { ChartLoadingComponent } from '../chart-loading/chart-loading.component';

export interface ChartRadialGaugeConfig {
  /** ID único del contenedor DOM para amCharts */
  chartId: string;
  /** Porcentaje actual o inicial (0 - 100) */
  percentage?: number;
  /** Color del arco de avance (ej: '#118FFE', '#00e5ff') */
  arcColor?: string;
  /** Color del arco de fondo / pista */
  trackColor?: string;
  /** Texto principal en el centro. Si se omite, muestra `${percentage}%` */
  centerLabel?: string;
  /** Subtítulo debajo del texto central (ej: 'Efectividad', 'Completado') */
  centerSub?: string;
  /** Mínimo del eje (default: 0) */
  min?: number;
  /** Máximo del eje (default: 100) */
  max?: number;
  /** Mostrar u ocultar etiquetas de porcentaje en el exterior del eje (default: true) */
  showAxisLabels?: boolean;
  /** Altura del contenedor en px (default: 220) */
  height?: number;
  /** Radio porcentual del radar respecto al contenedor (default: 76 para evitar corte de labels) */
  radius?: number;
  /** Padding inferior del gráfico en px (default: 30) */
  paddingBottom?: number;
}

export interface ChartRadialGaugeCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartRadialGaugeConfig;
}

@Component({
  selector: 'app-chart-radial-gauge',
  templateUrl: './chart-radial-gauge.component.html',
  styleUrls: ['./chart-radial-gauge.component.scss'],
  standalone: true,
  imports: [ChartLoadingComponent],
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => ChartRadialGaugeComponent)
    }
  ]
})
export class ChartRadialGaugeComponent extends DashboardBaseChart implements OnDestroy {
  @Input() config!: ChartRadialGaugeConfig;

  isLoading = true;

  private chart?: am5radar.RadarChart;
  private xAxis?: am5xy.ValueAxis<am5xy.AxisRenderer>;
  private valueDataItem?: am5.DataItem<am5xy.IValueAxisDataItem>;
  private centerLabelObj?: am5.Label;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  protected initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config?.chartId) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.config.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      this.root = root;

      const textColor = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);
      const arcColor = am5.color(this.config.arcColor ?? '#118FFE');
      const trackColor = am5.color(this.config.trackColor ?? '#9cc5ff');

      const gaugeRadius = this.config.radius ?? 76;
      const paddingBottom = this.config.paddingBottom ?? 30;

      const chart = root.container.children.push(
        am5radar.RadarChart.new(root, {
          panX: false,
          panY: false,
          startAngle: 180,
          endAngle: 360,
          innerRadius: am5.percent(70),
          radius: am5.percent(gaugeRadius),
          layout: root.verticalLayout,
          paddingLeft: 30,
          paddingRight: 30,
          paddingTop: 10,
          paddingBottom: paddingBottom,
        })
      );
      this.chart = chart;

      // Centrar verticalmente el semicírculo dentro del canvas para dar espacio armónico
      chart.radarContainer.set('y', am5.percent(70));

      const axisRenderer = am5radar.AxisRendererCircular.new(root, {
        innerRadius: am5.percent(70),
      });

      // Ocultar líneas de cuadrícula y marcas
      axisRenderer.grid.template.setAll({
        visible: false,
        forceHidden: true,
      });
      axisRenderer.ticks.template.setAll({
        visible: false,
        forceHidden: true,
      });

      // Formatear etiquetas exteriores como {value}% con holgura
      if (this.config.showAxisLabels !== false) {
        axisRenderer.labels.template.setAll({
          inside: false,
          fill: mutedColor,
          fontSize: 12,
          fontWeight: '500',
          radius: 8,
        });
        axisRenderer.labels.template.adapters.add('text', (text, target) => {
          const val = (target.dataItem as any)?.get('value');
          return val != null ? `${Math.round(val)}%` : text;
        });
      } else {
        axisRenderer.labels.template.setAll({ forceHidden: true });
      }

      const min = this.config.min ?? 0;
      const max = this.config.max ?? 100;

      const xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          maxDeviation: 0,
          min: min,
          max: max,
          strictMinMax: true,
          renderer: axisRenderer,
        })
      );
      this.xAxis = xAxis;

      // 1. Rango de fondo / pista (0 a 100%)
      const trackDataItem = xAxis.makeDataItem({
        above: false,
        value: min,
        endValue: max,
      });
      const trackRange = xAxis.createAxisRange(trackDataItem);
      trackRange.get('axisFill')?.setAll({
        visible: true,
        fill: trackColor,
        fillOpacity: 0.5,
      });
      trackRange.get('label')?.setAll({ forceHidden: true });
      trackRange.get('grid')?.setAll({ forceHidden: true });

      // 2. Rango activo dinámico (0 a percentage)
      const initialVal = this.config.percentage ?? 0;
      this.valueDataItem = xAxis.makeDataItem({
        above: true,
        value: min,
        endValue: 0,
      });
      const valueRange = xAxis.createAxisRange(this.valueDataItem);
      valueRange.get('axisFill')?.setAll({
        visible: true,
        fill: arcColor,
        fillOpacity: 1,
      });
      valueRange.get('label')?.setAll({ forceHidden: true });
      valueRange.get('grid')?.setAll({ forceHidden: true });

      // Etiqueta central (valor numérico y subtítulo)
      this.centerLabelObj = chart.radarContainer.children.push(
        am5.Label.new(root, {
          centerX: am5.percent(50),
          centerY: am5.percent(80),
          textAlign: 'center',
          text: this.buildCenterText(initialVal),
          fill: textColor,
          populateText: true,
        })
      );

      // Animación inicial del arco si viene con valor
      if (initialVal > 0) {
        this.valueDataItem.animate({
          key: 'endValue',
          to: initialVal,
          duration: 900,
          easing: am5.ease.out(am5.ease.cubic),
        });
      }

      chart.appear(500, 100);
    });
  }

  load(data: number | { percentage: number; centerLabel?: string; centerSub?: string }): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    let percentage = 0;
    if (typeof data === 'number') {
      percentage = data;
    } else if (data && typeof data.percentage === 'number') {
      percentage = data.percentage;
      if (data.centerLabel !== undefined) this.config.centerLabel = data.centerLabel;
      if (data.centerSub !== undefined) this.config.centerSub = data.centerSub;
    } else if (this.config?.percentage != null) {
      percentage = this.config.percentage;
    }

    this.config.percentage = percentage;

    if (this.valueDataItem) {
      this.valueDataItem.animate({
        key: 'endValue',
        to: percentage,
        duration: 900,
        easing: am5.ease.out(am5.ease.cubic),
      });
    }

    if (this.centerLabelObj) {
      this.centerLabelObj.set('text', this.buildCenterText(percentage));
    }

    this.isLoaded = true;
  }

  private buildCenterText(percentage?: number): string {
    const displayVal = this.config.centerLabel ?? (percentage != null ? `${percentage}%` : '');
    return [
      displayVal ? `[fontSize:28px fontWeight:800]${displayVal}[/]` : '',
      this.config.centerSub ? `[#9db8d7 fontSize:14px]${this.config.centerSub}[/]` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  ngOnDestroy(): void {
    this.root?.dispose();
  }
}
